<script lang="ts">
  import { onMount } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    listAuthorities,
    createAuthority,
    updateAuthority,
    deleteAuthority,
    ApiError,
  } from "../lib/api";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import type { Authority } from "@diy-avatar/shared/admin";

  let authorities: Authority[] = [];
  let loading = false;
  let error: string | null = null;
  let newName = "";

  function handleAuth(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  async function load() {
    loading = true;
    try {
      authorities = (await listAuthorities()).authorities;
      error = null;
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "Could not load authorities.";
    } finally {
      loading = false;
    }
  }

  async function doCreate() {
    error = null;
    if (!newName.trim()) {
      error = "name required";
      return;
    }
    try {
      await createAuthority({ name: newName.trim() });
      newName = "";
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "create failed";
    }
  }

  async function doRename(a: Authority) {
    const name = prompt(`Rename "${a.name}" to:`, a.name);
    if (!name || name === a.name) return;
    try {
      await updateAuthority(a.id, { name });
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "rename failed";
    }
  }

  async function doDelete(a: Authority) {
    if (!confirm(`Delete "${a.name}"? This cascades to its schools.`)) return;
    try {
      await deleteAuthority(a.id);
      await load();
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "delete failed";
    }
  }

  onMount(load);
</script>

<section>
  <Breadcrumb items={[{ label: "Authorities" }]} />
  <h1>Authorities (Landkreise)</h1>
  {#if error}<p class="error" data-testid="error">{error}</p>{/if}

  <div class="new" data-testid="new-authority">
    <h2>Create authority</h2>
    <div class="row">
      <input
        type="text"
        placeholder="Landkreis name"
        bind:value={newName}
        data-testid="new-authority-name"
      />
      <button on:click={doCreate} data-testid="new-authority-submit">Create</button>
    </div>
  </div>

  <table data-testid="authorities-table">
    <thead>
      <tr><th>id</th><th>name</th><th>created</th><th>actions</th></tr>
    </thead>
    <tbody>
      {#each authorities as a (a.id)}
        <tr data-testid="authority-row-{a.id}">
          <td>#{a.id}</td>
          <td>
            <a href="/authorities/{a.id}" use:link data-testid="authority-link-{a.id}">
              {a.name}
            </a>
          </td>
          <td>{new Date(a.created_at).toISOString().slice(0, 10)}</td>
          <td class="actions">
            <button on:click={() => doRename(a)}>rename</button>
            <button on:click={() => doDelete(a)}>delete</button>
          </td>
        </tr>
      {/each}
      {#if authorities.length === 0 && !loading}
        <tr><td colspan="4" class="empty">No authorities.</td></tr>
      {/if}
    </tbody>
  </table>
</section>

<style>
  h1 { margin: 0 0 1rem; }
  h2 { margin: 0 0 0.5rem; font-size: 1rem; }
  .new {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  input {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  table { width: 100%; border-collapse: collapse; background: #111827; }
  th, td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th { background: #0f172a; color: #9ca3af; }
  td a { color: #34d399; text-decoration: none; }
  td a:hover { text-decoration: underline; }
  .actions { display: flex; gap: 0.3rem; }
  .empty { color: #6b7280; text-align: center; }
  .error { color: #fca5a5; }
</style>
